import { useNavigate } from "react-router-dom"
import { formatNumber, formatOrdinal, MEASURE_COLOR_LIST, PATH_INFO } from "../utils/helper"
import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { sampleDefault, SamplesContext, SamplesResponseType, UpdateSamplesContext } from "../../context/samplesContext"
import { drawMeasurementPath } from "../utils/drawMeasurement"
import { format, parseISO } from "date-fns"
import { loadPreviewImage } from "../utils/loadPreviewImage"
import ReactLoading from "react-loading"

export function PastMeasurements() {
  const { sample } = useContext(SamplesContext)
  const navigate = useNavigate()

  const [relatedSamples, setRelatedSamples] = useState<SamplesResponseType[]>([])
  const [selectedSample, setSelectedSample] = useState<SamplesResponseType>(sampleDefault)
  const [sortOder, setSortOder] = useState<'asc' | 'desc'>('asc')
  const [sortedData, setSortedData] = useState<SamplesResponseType[]>([])

  useEffect(() => {
    if (!relatedSamples) return
    const sorted = [...relatedSamples].sort((a, b) => {
      const diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      return sortOder === 'asc' ? diff : -diff
    })
    setSortedData(sorted)
  }, [sortOder, relatedSamples])

  const handleSortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSortOder(e.target.value as 'asc' | 'desc')
  }, [])

  const fetchRelatedSamples = useCallback(async () => {
    if (!sample.part_number && !sample.sample_number) setRelatedSamples([])
    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${sample.id}/related-samples`, {
        method: "post",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ part_number: sample.part_number, sample_number: sample.sample_number, repeat: sample.repeat })
      })
      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      const data = await res.json();
      setRelatedSamples(data.relatedSamples)
    } catch (error) {
      console.log("ERROR FETCH")
    }
  }, [navigate, sample])

  useEffect(() => { fetchRelatedSamples() }, [fetchRelatedSamples])

  return (
    <div>
      <div className='flex items-center w-full'>
        <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
        <span className='text-[#3E3E3E] text-[16px]'>
          過去の採寸の表示
        </span>
      </div>
      <div className="flex flex-col items-start justify-center w-full pt-[25px] laptop-sm:flex-row">
        <div className="mr-[15px] h-[670px] w-full max-w-[1177px]">
          <div className="flex justify-end items-center w-full pb-[12px] space-x-[8px]">
            <label className="flex items-center">
              <input type="radio" name="sort" checked={sortOder === 'asc'} value={'asc'} className="w-[26px] h-[26px] accent-[#545899]" onChange={handleSortChange} />
              <span className="text-[14px] ml-[8px]">撮影日順(昇順)</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name="sort" checked={sortOder === 'desc'} value={'desc'} className="w-[26px] h-[26px] accent-[#545899]" onChange={handleSortChange} />
              <span className="text-[14px] ml-[8px]">撮影日順(降順)</span>
            </label>
          </div>
          <Samples samples={sortedData} selectedSample={selectedSample} setSelectedSample={setSelectedSample} />
        </div>
        <div className="w-full mt-[20px] laptop-sm:min-w-[604px] laptop-sm:max-w-[604px] laptop-sm:mt-0">
          <Tabele sample={selectedSample} />
          <div className="flex justify-end w-full mt-[15px]">
            <button onClick={() => navigate(PATH_INFO.HOME.PATH)} className="bg-black w-[280px] h-[47px] rounded-[50px]">
              <span className="text-white">検寸画面に戻る</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Samples({ samples, selectedSample, setSelectedSample }: { samples?: SamplesResponseType[], selectedSample?: SamplesResponseType, setSelectedSample: Dispatch<SetStateAction<SamplesResponseType>> }) {
  const formatDateTime = useCallback((dateString: string) => {
    try {
      const parsedDate = parseISO(dateString)
      if (Number.isNaN(parsedDate.getTime())) {
        return ''
      }
      return format(parsedDate, 'yyyy/MM/dd HH:mm')
    } catch (_error) {
      console.error('Failed to format date time:', _error)
      return ''
    }
  }, [])

  return (
    <div className="h-[632px] w-full overflow-y-scroll grid grid-cols-1 gap-x-[10px] gap-y-[20px] pr-[5px] max-sm:grid-cols-2 max-laptop-sm:grid-cols-3 desktop-sm:grid-cols-2 desktop-xmd:grid-cols-3 desktop-xlg:grid-cols-4 desktop-xxl:max-w-[1177px]">
      {!samples ? (
        <></>
      ) : (
        samples.map((el, index) => (
          <div key={index}>
            <div
              className={`flex flex-col items-center pt-[11px] px-2.5 shadow-md rounded-[8px] ${selectedSample ? selectedSample.id === el.id ? 'bg-[#E4E4E4]' : 'bg-white' : 'bg-white'}`}
              key={index}
            >
              <div className="flex flex-col w-full text-[14px]">
                <span>【品番】{el.part_number || '-'}</span>
                <span>【サンプルNo】{el.sample_number || '-'}</span>
                <span>【サンプル状況】{el.kind === 'final' ? 'FINAL' : formatOrdinal(el.time)}</span>
                <span>【リピート】{el.repeat}</span>
                <span>【サイズ】{el.size}</span>
                <span>【カラー】{el.color_no || '-'}</span>
                <span>【検寸日時】<span className="text-[12px]">{formatDateTime(el.created_at)}</span></span>
              </div>
              <SampleImagePreview sample={el} />
            </div>
            <button
              className="bg-[#333333] h-[37px] w-full rounded-[50px] text-white text-[12px] mt-[8px] shadow-md"
              onClick={() => setSelectedSample(el)}
            >寸法を確認</button>
          </div>)))}
    </div>
  )
}

const PREVIEW_LINE_WIDTH = 1
const FONT_MIN_SIZE = 7
const FONT_MAX_SIZE = 10
const FONT_RATIO = 0.06
const LABEL_RADIUS_RATIO = 0.55
const PREVIEW_BASE_WIDTH = 260
const PREVIEW_BASE_HEIGHT = 159
const PREVIEW_ASPECT_RATIO = PREVIEW_BASE_HEIGHT / PREVIEW_BASE_WIDTH

function SampleImagePreview({ sample }: { sample: SamplesResponseType }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const imageUrl = sample.sampleImages?.[0]?.url || ''

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateSize = (width: number, height: number) => {
      setContainerSize(prev => (prev.width !== width || prev.height !== height ? { width, height } : prev))
    }

    const applySize = () => {
      if (!containerRef.current) return
      const width = containerRef.current.clientWidth || PREVIEW_BASE_WIDTH
      const height = width * PREVIEW_ASPECT_RATIO
      updateSize(width, height)
    }

    applySize()

    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => applySize())
      observer.observe(container)
    }

    const handleWindowResize = () => applySize()
    window.addEventListener('resize', handleWindowResize)

    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }
    const target = containerRef.current
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        })
      },
      { threshold: 0.3 }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container || !isVisible) {
      if (!isVisible) setIsImageLoading(false)
      return
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      setIsImageLoading(false)
      return
    }

    if (!imageUrl) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      setIsImageLoading(false)
      return
    }

    setIsImageLoading(true)
    let isCancelled = false

    loadPreviewImage(imageUrl)
      .then(img => {
        if (isCancelled) return
        const width = containerSize.width || PREVIEW_BASE_WIDTH
        const height = containerSize.height || PREVIEW_BASE_HEIGHT
        const naturalWidth = img.width || width
        const naturalHeight = img.height || height

        if (!width || !height || !naturalWidth || !naturalHeight) {
          setIsImageLoading(false)
          return
        }

        const dpr = window.devicePixelRatio || 1
        canvas.width = width * dpr
        canvas.height = height * dpr
        canvas.style.width = `${width}px`
        canvas.style.height = `${height}px`
        ctx.resetTransform()
        ctx.scale(dpr, dpr)
        ctx.clearRect(0, 0, width, height)

        const widthRatio = width / naturalWidth
        const heightRatio = height / naturalHeight
        const scale = Math.min(widthRatio, heightRatio)
        const drawWidth = naturalWidth * scale
        const drawHeight = naturalHeight * scale
        const offsetX = (width - drawWidth) / 2
        const offsetY = (height - drawHeight) / 2

        ctx.drawImage(img, 0, 0, naturalWidth, naturalHeight, offsetX, offsetY, drawWidth, drawHeight)

        const scaleX = drawWidth / naturalWidth
        const scaleY = drawHeight / naturalHeight

        const measurements = sample.sampleMeasurements
          .filter(m => !m.is_deleted && m.result_point.length >= 2)
          .sort((a, b) => a.measure_index - b.measure_index)

        measurements.forEach((measurement, idx) => {
          const color = MEASURE_COLOR_LIST[idx % MEASURE_COLOR_LIST.length]?.color ?? '#000000'
          const displayIndex = idx + 1
          const scaledPoints = measurement.result_point.map(point => ({
            x: offsetX + point.x * scaleX,
            y: offsetY + point.y * scaleY
          }))

          drawMeasurementPath({
            ctx,
            points: scaledPoints,
            label: formatNumber(displayIndex),
            color,
            lineWidth: PREVIEW_LINE_WIDTH,
            fontMinSize: FONT_MIN_SIZE,
            fontMaxSize: FONT_MAX_SIZE,
            fontRatio: FONT_RATIO,
            labelRadiusRatio: LABEL_RADIUS_RATIO
          })
        })
        setIsImageLoading(false)
      })
      .catch(() => {
        if (isCancelled) return
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        setIsImageLoading(false)
      })

    return () => {
      isCancelled = true
    }
  }, [imageUrl, sample.sampleMeasurements, containerSize, isVisible])

  return (
    <div
      ref={containerRef}
      className="relative flex items-center justify-center w-full rounded-[9px] bg-white border border-[#E5E5E5] overflow-hidden my-[13px]"
      style={{ aspectRatio: `${PREVIEW_BASE_WIDTH} / ${PREVIEW_BASE_HEIGHT}` }}
    >
      {imageUrl ? (
        <>
          <canvas ref={canvasRef} className="w-full h-full" />
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
              <ReactLoading type="spin" color="#545899" width={32} height={32} />
            </div>
          )}
        </>
      ) : (
        <span className="text-[12px] text-[#999999]">画像なし</span>
      )}
    </div>
  )
}

type DataType = {
  measure_index?: number
  number: string
  measure_name?: string
  spec?: number
  measure_result?: number
  diff?: number
  judge?: string
}

function Tabele({ sample }: { sample: SamplesResponseType }) {
  const data: DataType[] = useMemo(() => [
    { number: '①' },
    { number: '②' },
    { number: '③' },
    { number: '④' },
    { number: '⑤' },
    { number: '⑥' },
    { number: '⑦' },
    { number: '⑧' },
    { number: '⑨' },
    { number: '⑩' },
    { number: '⑪' },
    { number: '⑫' },
    { number: '⑬' },
    { number: '⑭' },
    { number: '⑮' },
    { number: '⑯' },
    { number: '⑰' },
    { number: '⑱' },
    { number: '⑲' },
    { number: '⑳' },
    { number: '㉑' },
    { number: '㉒' },
    { number: '㉓' },
    { number: '㉔' },
    { number: '㉕' },
    { number: '㉖' },
    { number: '㉗' },
    { number: '㉘' },
    { number: '㉙' },
    { number: '㉚' }
  ], [])
  const [tableData, setTableData] = useState(data)
  const { fetchDiff } = useContext(UpdateSamplesContext)
  const { diff } = useContext(SamplesContext)

  useEffect(() => {
    if (sample) {
      fetchDiff(sample)
    }
  }, [sample, fetchDiff])

  useEffect(() => {
    const newData = data.map((item, index) => {
      const sampleData = sample?.sampleMeasurements[index]
      const diffData = diff?.data?.points.find((d) => d.measure_name === sampleData?.measure_name)

      let judge: '' | 'OK' | 'NG' | '-'
      if (!diffData) {
        judge = ''
      } else if (diffData.validate != null && diffData.spec != null) {
        judge = diffData.validate ? 'OK' : 'NG'
      } else {
        judge = '-'
      }

      if (sampleData) {
        return {
          ...item,
          measure_index: sampleData.measure_index,
          measure_name: sampleData?.measure_name ?? '',
          spec: diffData?.spec ?? 0,
          measure_result: sampleData?.result_real,
          diff: diffData?.diff ?? 0,
          judge,
        }
      }
      return item
    })

    const sortedData = [...newData].sort((a, b) => {
      const ia = a.measure_index ?? Infinity
      const ib = b.measure_index ?? Infinity
      return ia - ib
    })

    setTableData(sortedData)
  }, [sample, data, diff])

  return (
    <div className="h-[670px] bg-[#333333] rounded-[20px] pt-[18px] pb-6 px-5">
      <div className='flex items-center w-full'>
        <div className='w-2 h-[35px] rounded-sm bg-white mr-[5px]' ></div>
        <span className='text-white text-[16px]'>
          採寸結果
        </span>
      </div>
      <div className="w-full h-[36px] bg-white mt-[15px]">
        <span className="w-full h-full flex items-center justify-center font-medium">
          {sample.kind === 'final' ? 'FINAL' : sample.time !== 0 ? formatOrdinal(sample.time) : ''}
        </span>
      </div>
      <div className="h-[536px] overflow-y-scroll border border-gray-400">
        <table className="w-full">
          <thead className="bg-[#333333] h-[23px]">
            <tr className="sticky inset-0 bg-[#CCCCCC] text-[12px]">
              <th className="sticky top-0 w-[31px]">No</th>
              <th className="sticky top-0 w-[150px] before:content-[''] before:absolute before:inset-0 before:border-l before:border-[#333333]">
                検寸項目
              </th>
              <th className="sticky top-0 w-[100px] before:content-[''] before:absolute before:inset-0 before:border-l before:border-[#333333]">
                指示寸法
              </th>
              <th className="sticky top-0 w-[100px] before:content-[''] before:absolute before:inset-0 before:border-l before:border-[#333333]">
                検寸結果
              </th>
              <th className="sticky top-0 w-[84px] before:content-[''] before:absolute before:inset-0 before:border-l before:border-[#333333]">
                差寸
              </th>
              <th className="sticky top-0 w-[84px] before:content-[''] before:absolute before:inset-0 before:border-l before:border-[#333333]">
                判定
              </th>
            </tr>
          </thead>
          <tbody className="bg-[#333333] text-white">
            {tableData.map((el, index) => {
              const hasData = Boolean(el.measure_name || el.measure_result)
              const rowBg = index % 2 === 1 ? 'bg-[#F3F3F31A]' : 'bg-[#333333]'

              return (
                <tr key={index} className={`text-[14px] ${rowBg}`}>
                  <td className="h-[30px] border border-l-0 border-t-0 border-gray-400 text-center text-[16px ]">{formatNumber(index + 1)}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.measure_name}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{hasData ? (el.spec !== 0 ? el.spec : '-') : ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{hasData ? (el.measure_result !== 0 ? el.measure_result : '-') : ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{hasData ? (el.diff !== 0 ? el.diff : '-') : ""}</td>
                  <td className="border border-r-0 border-t-0 border-gray-400 text-center">{el.judge}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
